/**
 * Success screen for the app / payment-only purchase flow.
 *
 * When a viewer arrives from a native app (e.g. a Roku QR-code deep link) they
 * are on a throwaway browser tab on their phone. After a successful purchase or
 * registration the site should NOT reload (there is nothing to browse in
 * payment-only mode) — instead we show a confirmation and tell them they can
 * walk away. Their device signs in automatically in the background via the
 * auto-login poll once the subscriber/device link is created.
 */

import { isAuth } from "./authHelper";

// Whether the viewer was already authenticated when the payment form opened.
// Captured before the purchase stores fresh credentials so the success screen
// can tell whether the "your device will sign in automatically" line applies.
let wasLoggedInBeforePurchase = false;

/**
 * Snapshot the auth state before a purchase begins. Call when the payment form
 * is shown, before any purchase writes a loginToken.
 */
export function rememberAuthBeforePurchase() {
  wasLoggedInBeforePurchase = isAuth();
}

/**
 * True only on payment-only sites, detected from the payment-only body class
 * (added by applyPaymentOnlyStyles when the tenant's is_site_payment_only flag
 * is on). Normal sites never show the success screen.
 */
export function isPaymentOnlySite() {
  if (typeof document === "undefined" || !document.body) {
    return false;
  }
  return document.body.classList.contains("payment-only-mode");
}

/**
 * Render a full-screen success confirmation. Clears any lingering payment
 * forms / player chrome first so only the confirmation is visible.
 */
export function showPurchaseSuccess({ isFree = false } = {}) {
  if (typeof document === "undefined") return;

  // Guard against double injection (e.g. multiple success callbacks firing).
  if (document.getElementById("purchase-success-screen")) return;

  const heading = isFree
    ? "Registered successfully!"
    : "Payment successful!";

  // The device auto-login only applies when the viewer was NOT already signed
  // in (a fresh subscriber whose device still needs to be linked).
  const subtext = wasLoggedInBeforePurchase
    ? "You can now close this window."
    : "You can now close this window. Your device will sign in automatically.";

  // Remove payment forms, gateway modals and the rest of the page.
  document
    .querySelectorAll(
      "#stripePurchaseCard, #PaypalPurchaseCard, #DPOPayPurchaseCard, #authorizeNetPurchaseCard, .purchase-card, .modal"
    )
    .forEach((el) => el.remove());
  const appContainer = document.getElementById("app-container");
  if (appContainer) appContainer.style.display = "none";

  const style = document.createElement("style");
  style.id = "purchase-success-style";
  style.textContent = `
    #purchase-success-screen {
      position: fixed; inset: 0; z-index: 2147483647;
      display: flex; align-items: center; justify-content: center;
      background: #ffffff; padding: 24px; box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    #purchase-success-screen .purchase-success-card {
      max-width: 440px; width: 100%; text-align: center;
    }
    #purchase-success-screen .purchase-success-icon {
      margin: 0 auto 24px; width: 88px; height: 88px; border-radius: 50%;
      background: #eaf7f0; display: flex; align-items: center; justify-content: center;
    }
    #purchase-success-screen .purchase-success-title {
      margin: 0 0 12px; font-size: 26px; line-height: 1.25; font-weight: 700; color: #111111;
    }
    #purchase-success-screen .purchase-success-subtext {
      margin: 0; font-size: 16px; line-height: 1.55; color: #555555;
    }
  `;
  document.head.appendChild(style);

  const screen = document.createElement("div");
  screen.id = "purchase-success-screen";
  screen.setAttribute("role", "status");
  screen.setAttribute("aria-live", "polite");
  screen.innerHTML = `
    <div class="purchase-success-card">
      <div class="purchase-success-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="52" height="52" fill="none"
             stroke="#22a06b" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 6L9 17l-5-5"></path>
        </svg>
      </div>
      <h1 class="purchase-success-title">${heading}</h1>
      <p class="purchase-success-subtext">${subtext}</p>
    </div>
  `;
  document.body.appendChild(screen);
}

/**
 * If the purchase came from the app / payment-only flow, show the success
 * screen and return true (so the caller skips its reload/redirect). Returns
 * false for the normal web flow, where the caller should proceed as before.
 */
export function finalizePurchaseSuccess(opts = {}) {
  if (isPaymentOnlySite()) {
    showPurchaseSuccess(opts);
    return true;
  }
  return false;
}
